import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Form, 
    Input, 
    Button, 
    message, 
    Typography, 
    Space, 
    Row, 
    Col, 
    Upload, 
    Select,
    Divider,
    Spin,
    Tooltip
} from 'antd';
import { 
    SaveOutlined, 
    PhoneOutlined, 
    EnvironmentOutlined, 
    GlobalOutlined, 
    UploadOutlined,
    MailOutlined,
    ShopOutlined,
    FileTextOutlined,
    IdcardOutlined,
    DollarOutlined,
    LinkOutlined,
    FileImageOutlined,
    PictureOutlined,
    TagOutlined,
    EditOutlined,
    QuestionCircleOutlined,
    InfoCircleOutlined,
    MinusCircleOutlined,
    PlusOutlined
} from '@ant-design/icons';
import companyService from '../../services/companyService';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import env from '../../env';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const businessTypes = [
    'Individual',
    'Sole Proprietorship',
    'Partnership',
    'Private Limited Company',
    'Public Limited Company',
    'LLC',
    'Corporation',
    'Other'
];

const CompanyInformation = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [company, setCompany] = useState(null);
    const [logoFileList, setLogoFileList] = useState([]);
    const [bannerFileList, setBannerFileList] = useState([]);
    const [socialMediaLinks, setSocialMediaLinks] = useState(['']);

    useEffect(() => {
        fetchCompany();
    }, []);

    const fetchCompany = async () => {
        try {
            setInitialLoading(true);
            const response = await companyService.getCompany();
            if (response.company) {
                setCompany(response.company);
                const companyData = response.company;
                
                // Set form values
                form.setFieldsValue({
                    company_name: companyData.company_name,
                    business_type: companyData.business_type,
                    company_registration_number: companyData.company_registration_number,
                    tax_ntn_number: companyData.tax_ntn_number,
                    business_address: companyData.business_address,
                    email_address: companyData.email_address,
                    phone_whatsapp: companyData.phone_whatsapp,
                    website: companyData.website,
                    short_tagline: companyData.short_tagline,
                    company_description: companyData.company_description,
                });

                // Set image file lists
                if (companyData.company_logo_url) {
                    // Storage::url() returns path like /storage/..., so we need to prepend BACKEND_URL
                    const logoUrl = companyData.company_logo_url.startsWith('http') 
                        ? companyData.company_logo_url 
                        : env.BACKEND_URL + companyData.company_logo_url;
                    setLogoFileList([{
                        uid: '-1',
                        name: 'company_logo.jpg',
                        status: 'done',
                        url: logoUrl,
                    }]);
                }

                if (companyData.banner_image_url) {
                    // Storage::url() returns path like /storage/..., so we need to prepend BACKEND_URL
                    const bannerUrl = companyData.banner_image_url.startsWith('http') 
                        ? companyData.banner_image_url 
                        : env.BACKEND_URL + companyData.banner_image_url;
                    setBannerFileList([{
                        uid: '-1',
                        name: 'banner.jpg',
                        status: 'done',
                        url: bannerUrl,
                    }]);
                }

                // Set social media links
                if (companyData.social_media_links && Array.isArray(companyData.social_media_links)) {
                    setSocialMediaLinks(companyData.social_media_links.length > 0 
                        ? companyData.social_media_links 
                        : ['']);
                }
            }
        } catch (error) {
            if (error.response?.status !== 404) {
                message.error('Failed to load company information');
            }
        } finally {
            setInitialLoading(false);
        }
    };

    const handleLogoUpload = (info) => {
        let fileList = [...info.fileList];
        fileList = fileList.slice(-1); // Only keep the last file
        fileList = fileList.map(file => {
            if (file.response) {
                file.url = file.response.url;
            }
            return file;
        });
        setLogoFileList(fileList);
    };

    const handleBannerUpload = (info) => {
        let fileList = [...info.fileList];
        fileList = fileList.slice(-1); // Only keep the last file
        fileList = fileList.map(file => {
            if (file.response) {
                file.url = file.response.url;
            }
            return file;
        });
        setBannerFileList(fileList);
    };

    const handleLogoRemove = async (file) => {
        try {
            // If there's an existing logo, delete it from backend
            if (company?.company_logo) {
                const response = await companyService.deleteCompanyLogo();
                
                // Update company state with the response from backend
                if (response && response.company) {
                    setCompany(response.company);
                } else {
                    // Fallback: manually update if response structure is different
                    setCompany({
                        ...company,
                        company_logo: null,
                        company_logo_url: null
                    });
                }
                
                message.success('Company logo deleted successfully');
            }
            
            // Clear local state
            setLogoFileList([]);
        } catch (error) {
            console.error('Error deleting company logo:', error);
            message.error(error.response?.data?.message || 'Failed to delete company logo');
            // Don't remove from file list if deletion failed
            return false;
        }
        return true;
    };

    const handleBannerRemove = async (file) => {
        try {
            // If there's an existing banner, delete it from backend
            if (company?.banner_image) {
                const response = await companyService.deleteBannerImage();
                
                // Update company state with the response from backend
                if (response && response.company) {
                    setCompany(response.company);
                } else {
                    // Fallback: manually update if response structure is different
                    setCompany({
                        ...company,
                        banner_image: null,
                        banner_image_url: null
                    });
                }
                
                message.success('Banner image deleted successfully');
            }
            
            // Clear local state
            setBannerFileList([]);
        } catch (error) {
            console.error('Error deleting banner image:', error);
            message.error(error.response?.data?.message || 'Failed to delete banner image');
            // Don't remove from file list if deletion failed
            return false;
        }
        return true;
    };

    const addSocialMediaLink = () => {
        setSocialMediaLinks([...socialMediaLinks, '']);
    };

    const removeSocialMediaLink = (index) => {
        const newLinks = socialMediaLinks.filter((_, i) => i !== index);
        setSocialMediaLinks(newLinks.length > 0 ? newLinks : ['']);
    };

    const updateSocialMediaLink = (index, value) => {
        const newLinks = [...socialMediaLinks];
        newLinks[index] = value;
        setSocialMediaLinks(newLinks);
    };

    // URL validation function
    const validateURL = (url) => {
        if (!url || url.trim() === '') {
            return true; // Empty is allowed
        }
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    // Validate all social media links before submission
    const validateSocialMediaLinks = () => {
        const invalidLinks = [];
        socialMediaLinks.forEach((link, index) => {
            if (link && link.trim() !== '' && !validateURL(link)) {
                invalidLinks.push({ index, link });
            }
        });
        return invalidLinks;
    };

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            
            // Validate social media links before submission
            const invalidLinks = validateSocialMediaLinks();
            if (invalidLinks.length > 0) {
                message.error(`Please enter valid URLs for social media links. Invalid links found at position(s): ${invalidLinks.map(il => il.index + 1).join(', ')}`);
                setLoading(false);
                return;
            }
            
            // Get logo file if uploaded
            const logoFile = logoFileList.length > 0 && logoFileList[0].originFileObj 
                ? logoFileList[0].originFileObj 
                : null;

            // Get banner file if uploaded
            const bannerFile = bannerFileList.length > 0 && bannerFileList[0].originFileObj 
                ? bannerFileList[0].originFileObj 
                : null;

            // Prepare form data - include all fields even if empty
            const formData = {
                ...values,
                social_media_links: socialMediaLinks.filter(link => link.trim() !== ''),
            };

            if (logoFile) {
                formData.company_logo = logoFile;
            }

            if (bannerFile) {
                formData.banner_image = bannerFile;
            }

            // Debug: Log what we're sending
            console.log('Sending company data:', formData);
            console.log('Form values:', values);

            const response = await companyService.updateCompany(formData);
            
            setCompany(response.company);
            message.success('Company information updated successfully');
            
            // Update file lists with new URLs
            if (response.company.company_logo_url) {
                // Storage::url() returns path like /storage/..., so we need to prepend BACKEND_URL
                const logoUrl = response.company.company_logo_url.startsWith('http') 
                    ? response.company.company_logo_url 
                    : env.BACKEND_URL + response.company.company_logo_url;
                setLogoFileList([{
                    uid: '-1',
                    name: 'company_logo.jpg',
                    status: 'done',
                    url: logoUrl,
                }]);
            }

            if (response.company.banner_image_url) {
                // Storage::url() returns path like /storage/..., so we need to prepend BACKEND_URL
                const bannerUrl = response.company.banner_image_url.startsWith('http') 
                    ? response.company.banner_image_url 
                    : env.BACKEND_URL + response.company.banner_image_url;
                setBannerFileList([{
                    uid: '-1',
                    name: 'banner.jpg',
                    status: 'done',
                    url: bannerUrl,
                }]);
            }
        } catch (error) {
            console.error('Company update error:', error);
            console.error('Error response:', error.response);
            console.error('Error data:', error.response?.data);
            
            if (error.response?.data?.errors) {
                const errors = error.response.data.errors;
                Object.keys(errors).forEach(key => {
                    const errorMessages = Array.isArray(errors[key]) ? errors[key] : [errors[key]];
                    errorMessages.forEach(msg => {
                        message.error(`${key.charAt(0).toUpperCase() + key.slice(1)}: ${msg}`);
                    });
                });
            } else if (error.response?.data?.message) {
                message.error(error.response.data.message);
            } else if (error.message) {
                message.error(`Error: ${error.message}`);
            } else {
                message.error('Failed to update company information. Please check your connection and try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <DashboardLayout>
            <div style={{ marginBottom: 32 }}>
                <Title level={2} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                    🏢 Company Information
                </Title>
                <Typography.Paragraph style={{ color: '#595959', fontSize: '16px', marginTop: 8, marginBottom: 0 }}>
                    Manage your company profile and business details. Keep your information up to date!
                </Typography.Paragraph>
            </div>

            <Spin spinning={loading || initialLoading}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                        {/* Basic Company Information */}
                        <Card 
                            className="form-section"
                            style={{ 
                                marginBottom: 32,
                                borderRadius: '16px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                                border: '1px solid #e8f4f8',
                                transition: 'all 0.3s ease',
                                overflow: 'hidden'
                            }}
                            bodyStyle={{ padding: '32px' }}
                        >
                            <div style={{ 
                                borderBottom: '2px solid #f0f7ff',
                                paddingBottom: 20,
                                marginBottom: 28,
                                background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                                margin: '-32px -32px 28px -32px',
                                padding: '24px 32px'
                            }}>
                                <Space align="center" size="middle">
                                    <div style={{
                                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                        padding: '12px',
                                        borderRadius: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                                    }}>
                                        <ShopOutlined style={{ color: 'white', fontSize: '24px' }} />
                                    </div>
                                    <div>
                                        <Title level={4} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                                            Basic Company Information
                                        </Title>
                                        <Text type="secondary" style={{ fontSize: '15px', color: '#595959', display: 'block', marginTop: 4 }}>
                                            Your company's essential details
                                        </Text>
                                    </div>
                                </Space>
                            </div>
                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="company_name"
                                        label={
                                            <Space size="small">
                                                <span style={{ fontWeight: 500, fontSize: '15px' }}>Company Name</span>
                                                <Tooltip title="The official name of your company or business">
                                                    <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                                </Tooltip>
                                            </Space>
                                        }
                                        rules={[{ max: 255, message: 'Company name must be less than 255 characters' }]}
                                        extra={
                                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                                🏢 Enter your company's official name
                                            </Text>
                                        }
                                    >
                                        <Input 
                                            prefix={<ShopOutlined style={{ color: '#1890ff' }} />} 
                                            placeholder="e.g., Al-Fateh Livestock Traders"
                                            size="large"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="business_type"
                                        label={
                                            <Space size="small">
                                                <span style={{ fontWeight: 500, fontSize: '15px' }}>Business Type</span>
                                                <Tooltip title="Select the legal structure of your business">
                                                    <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                                </Tooltip>
                                            </Space>
                                        }
                                        extra={
                                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                                📋 Choose your business structure (optional)
                                            </Text>
                                        }
                                    >
                                        <Select 
                                            placeholder="Select business type..."
                                            allowClear
                                            size="large"
                                            style={{ borderRadius: '8px' }}
                                        >
                                            {businessTypes.map(type => (
                                                <Option key={type} value={type}>{type}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="company_registration_number"
                                        label={
                                            <Space size="small">
                                                <span style={{ fontWeight: 500, fontSize: '15px' }}>Company Registration Number</span>
                                                <Tooltip title="Your official company registration number for verification purposes">
                                                    <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                                </Tooltip>
                                            </Space>
                                        }
                                        rules={[{ max: 255, message: 'Registration number must be less than 255 characters' }]}
                                        extra={
                                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                                🆔 Official registration number (optional)
                                            </Text>
                                        }
                                    >
                                        <Input 
                                            prefix={<IdcardOutlined style={{ color: '#1890ff' }} />} 
                                            placeholder="Enter registration number"
                                            size="large"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="tax_ntn_number"
                                        label={
                                            <Space size="small">
                                                <span style={{ fontWeight: 500, fontSize: '15px' }}>Tax/NTN Number</span>
                                                <Tooltip title="Your tax or NTN (National Tax Number) for business compliance">
                                                    <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                                </Tooltip>
                                            </Space>
                                        }
                                        rules={[{ max: 255, message: 'Tax/NTN number must be less than 255 characters' }]}
                                        extra={
                                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                                💰 Tax identification number (optional)
                                            </Text>
                                        }
                                    >
                                        <Input 
                                            prefix={<DollarOutlined style={{ color: '#1890ff' }} />} 
                                            placeholder="Enter tax/NTN number"
                                            size="large"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item
                                name="business_address"
                                label={
                                    <Space size="small">
                                        <span style={{ fontWeight: 500, fontSize: '15px' }}>Business Address</span>
                                        <Tooltip title="The physical address where your business is located">
                                            <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                        </Tooltip>
                                    </Space>
                                }
                                rules={[{ max: 1000, message: 'Address must be less than 1000 characters' }]}
                                extra={
                                    <Text type="secondary" style={{ fontSize: '13px' }}>
                                        📍 Enter your complete business address
                                    </Text>
                                }
                            >
                                <TextArea 
                                    rows={4}
                                    placeholder="Enter your full business address..."
                                    size="large"
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Item>
                        </Card>

                        <Divider style={{ margin: '40px 0', borderColor: '#e8f4f8' }} />

                        {/* Contact Information */}
                        <Card 
                            className="form-section"
                            style={{ 
                                marginBottom: 32,
                                borderRadius: '16px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                                border: '1px solid #e8f4f8',
                                transition: 'all 0.3s ease',
                                overflow: 'hidden'
                            }}
                            bodyStyle={{ padding: '32px' }}
                        >
                            <div style={{ 
                                borderBottom: '2px solid #f6ffed',
                                paddingBottom: 20,
                                marginBottom: 28,
                                background: 'linear-gradient(135deg, #f6ffed 0%, #ffffff 100%)',
                                margin: '-32px -32px 28px -32px',
                                padding: '24px 32px'
                            }}>
                                <Space align="center" size="middle">
                                    <div style={{
                                        background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                                        padding: '12px',
                                        borderRadius: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 4px 12px rgba(82, 196, 26, 0.3)'
                                    }}>
                                        <MailOutlined style={{ color: 'white', fontSize: '24px' }} />
                                    </div>
                                    <div>
                                        <Title level={4} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                                            Contact Information
                                        </Title>
                                        <Text type="secondary" style={{ fontSize: '15px', color: '#595959', display: 'block', marginTop: 4 }}>
                                            How customers and partners can reach you
                                        </Text>
                                    </div>
                                </Space>
                            </div>
                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="email_address"
                                        label={
                                            <Space size="small">
                                                <span style={{ fontWeight: 500, fontSize: '15px' }}>Email Address</span>
                                                <Tooltip title="Your primary business email for customer inquiries">
                                                    <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                                </Tooltip>
                                            </Space>
                                        }
                                        rules={[
                                            { type: 'email', message: 'Please enter a valid email address' },
                                            { max: 255, message: 'Email must be less than 255 characters' }
                                        ]}
                                        extra={
                                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                                📧 Primary business contact email
                                            </Text>
                                        }
                                    >
                                        <Input 
                                            prefix={<MailOutlined style={{ color: '#52c41a' }} />} 
                                            placeholder="business@example.com"
                                            size="large"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="phone_whatsapp"
                                        label={
                                            <Space size="small">
                                                <span style={{ fontWeight: 500, fontSize: '15px' }}>Phone / WhatsApp</span>
                                                <Tooltip title="Your business phone number or WhatsApp contact">
                                                    <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                                </Tooltip>
                                            </Space>
                                        }
                                        rules={[{ max: 20, message: 'Phone number must be less than 20 characters' }]}
                                        extra={
                                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                                📞 Include country code (e.g., +92 300 1234567)
                                            </Text>
                                        }
                                    >
                                        <Input 
                                            prefix={<PhoneOutlined style={{ color: '#52c41a' }} />} 
                                            placeholder="+92 300 1234567"
                                            size="large"
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item
                                name="website"
                                label={
                                    <Space size="small">
                                        <span style={{ fontWeight: 500, fontSize: '15px' }}>Website</span>
                                        <Tooltip title="Your company website URL (optional)">
                                            <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                        </Tooltip>
                                    </Space>
                                }
                                rules={[
                                    { type: 'url', message: 'Please enter a valid URL' },
                                    { max: 255, message: 'Website URL must be less than 255 characters' }
                                ]}
                                extra={
                                    <Text type="secondary" style={{ fontSize: '13px' }}>
                                        🌐 Your company website URL (optional)
                                    </Text>
                                }
                            >
                                <Input 
                                    prefix={<GlobalOutlined style={{ color: '#52c41a' }} />} 
                                    placeholder="https://your-website.com"
                                    size="large"
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Item>
                        </Card>

                        <Divider style={{ margin: '40px 0', borderColor: '#e8f4f8' }} />

                        {/* Company Description */}
                        <Card 
                            className="form-section"
                            style={{ 
                                marginBottom: 32,
                                borderRadius: '16px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                                border: '1px solid #e8f4f8',
                                transition: 'all 0.3s ease',
                                overflow: 'hidden'
                            }}
                            bodyStyle={{ padding: '32px' }}
                        >
                            <div style={{ 
                                borderBottom: '2px solid #f0f7ff',
                                paddingBottom: 20,
                                marginBottom: 28,
                                background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                                margin: '-32px -32px 28px -32px',
                                padding: '24px 32px'
                            }}>
                                <Space align="center" size="middle">
                                    <div style={{
                                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                        padding: '12px',
                                        borderRadius: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                                    }}>
                                        <FileTextOutlined style={{ color: 'white', fontSize: '24px' }} />
                                    </div>
                                    <div>
                                        <Title level={4} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                                            Company Description
                                        </Title>
                                        <Text type="secondary" style={{ fontSize: '15px', color: '#595959', display: 'block', marginTop: 4 }}>
                                            Tell others about your company
                                        </Text>
                                    </div>
                                </Space>
                            </div>
                            <Form.Item
                                name="short_tagline"
                                label={
                                    <Space size="small">
                                        <span style={{ fontWeight: 500, fontSize: '15px' }}>Short Tagline</span>
                                        <Tooltip title="A brief, memorable tagline that describes your company">
                                            <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                        </Tooltip>
                                    </Space>
                                }
                                rules={[{ max: 255, message: 'Tagline must be less than 255 characters' }]}
                                extra={
                                    <Text type="secondary" style={{ fontSize: '13px' }}>
                                        🏷️ A catchy one-liner about your company (optional)
                                    </Text>
                                }
                            >
                                <Input 
                                    prefix={<TagOutlined style={{ color: '#1890ff' }} />} 
                                    placeholder="e.g., Trusted Breeders of Premium Goats"
                                    size="large"
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Item>

                            <Form.Item
                                name="company_description"
                                label={
                                    <Space size="small">
                                        <span style={{ fontWeight: 500, fontSize: '15px' }}>Company Description</span>
                                        <Tooltip title="Share your company's story, mission, and what makes you unique">
                                            <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                        </Tooltip>
                                    </Space>
                                }
                                rules={[
                                    { max: 1000, message: 'Description must be less than 1000 characters' }
                                ]}
                                extra={
                                    <Text type="secondary" style={{ fontSize: '13px' }}>
                                        ✍️ Share your company's story, mission, and what makes you unique (maximum 1000 characters)
                                    </Text>
                                }
                            >
                                <TextArea 
                                    rows={6}
                                    placeholder="Tell us about your company... Share your story, mission, values, and what makes your business special!"
                                    showCount
                                    maxLength={1000}
                                    size="large"
                                    style={{ borderRadius: '8px' }}
                                />
                            </Form.Item>
                        </Card>

                        <Divider style={{ margin: '40px 0', borderColor: '#e8f4f8' }} />

                        {/* Media & Branding */}
                        <Card 
                            className="form-section"
                            style={{ 
                                marginBottom: 32,
                                borderRadius: '16px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                                border: '1px solid #e8f4f8',
                                transition: 'all 0.3s ease',
                                overflow: 'hidden'
                            }}
                            bodyStyle={{ padding: '32px' }}
                        >
                            <div style={{ 
                                borderBottom: '2px solid #fff7e6',
                                paddingBottom: 20,
                                marginBottom: 28,
                                background: 'linear-gradient(135deg, #fff7e6 0%, #ffffff 100%)',
                                margin: '-32px -32px 28px -32px',
                                padding: '24px 32px'
                            }}>
                                <Space align="center" size="middle">
                                    <div style={{
                                        background: 'linear-gradient(135deg, #faad14 0%, #d48806 100%)',
                                        padding: '12px',
                                        borderRadius: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 4px 12px rgba(250, 173, 20, 0.3)'
                                    }}>
                                        <PictureOutlined style={{ color: 'white', fontSize: '24px' }} />
                                    </div>
                                    <div>
                                        <Title level={4} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                                            Media & Branding
                                        </Title>
                                        <Text type="secondary" style={{ fontSize: '15px', color: '#595959', display: 'block', marginTop: 4 }}>
                                            Showcase your company with professional images
                                        </Text>
                                    </div>
                                </Space>
                            </div>
                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item 
                                        label={
                                            <Space size="small">
                                                <span style={{ fontWeight: 500, fontSize: '15px' }}>Company Logo</span>
                                                <Tooltip title="Upload your company logo. JPG or PNG format, maximum 2 MB">
                                                    <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                                </Tooltip>
                                            </Space>
                                        }
                                        extra={
                                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                                🖼️ Upload your company logo (JPG/PNG, ≤ 2 MB)
                                            </Text>
                                        }
                                    >
                                        <Upload
                                            listType="picture-card"
                                            fileList={logoFileList}
                                            onChange={handleLogoUpload}
                                            onRemove={handleLogoRemove}
                                            beforeUpload={() => false} // Prevent auto upload
                                            maxCount={1}
                                            accept="image/jpeg,image/png,image/jpg"
                                            style={{ borderRadius: '8px' }}
                                        >
                                            {logoFileList.length < 1 && (
                                                <div style={{ textAlign: 'center' }}>
                                                    <UploadOutlined style={{ fontSize: '32px', color: '#faad14' }} />
                                                    <div style={{ marginTop: 8, color: '#595959', fontWeight: 500 }}>Upload Logo</div>
                                                </div>
                                            )}
                                        </Upload>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item 
                                        label={
                                            <Space size="small">
                                                <span style={{ fontWeight: 500, fontSize: '15px' }}>Banner Image</span>
                                                <Tooltip title="Upload a banner image for your company profile. JPG or PNG format, maximum 5 MB">
                                                    <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                                </Tooltip>
                                            </Space>
                                        }
                                        extra={
                                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                                🎨 Upload a banner image (JPG/PNG, ≤ 5 MB)
                                            </Text>
                                        }
                                    >
                                        <Upload
                                            listType="picture-card"
                                            fileList={bannerFileList}
                                            onChange={handleBannerUpload}
                                            onRemove={handleBannerRemove}
                                            beforeUpload={() => false} // Prevent auto upload
                                            maxCount={1}
                                            accept="image/jpeg,image/png,image/jpg"
                                            style={{ borderRadius: '8px' }}
                                        >
                                            {bannerFileList.length < 1 && (
                                                <div style={{ textAlign: 'center' }}>
                                                    <UploadOutlined style={{ fontSize: '32px', color: '#faad14' }} />
                                                    <div style={{ marginTop: 8, color: '#595959', fontWeight: 500 }}>Upload Banner</div>
                                                </div>
                                            )}
                                        </Upload>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        <Divider style={{ margin: '40px 0', borderColor: '#e8f4f8' }} />

                        {/* Social Media Links */}
                        <Card 
                            className="form-section"
                            style={{ 
                                marginBottom: 32,
                                borderRadius: '16px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                                border: '1px solid #e8f4f8',
                                transition: 'all 0.3s ease',
                                overflow: 'hidden'
                            }}
                            bodyStyle={{ padding: '32px' }}
                        >
                            <div style={{ 
                                borderBottom: '2px solid #f9f0ff',
                                paddingBottom: 20,
                                marginBottom: 28,
                                background: 'linear-gradient(135deg, #f9f0ff 0%, #ffffff 100%)',
                                margin: '-32px -32px 28px -32px',
                                padding: '24px 32px'
                            }}>
                                <Space align="center" size="middle">
                                    <div style={{
                                        background: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)',
                                        padding: '12px',
                                        borderRadius: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 4px 12px rgba(114, 46, 209, 0.3)'
                                    }}>
                                        <LinkOutlined style={{ color: 'white', fontSize: '24px' }} />
                                    </div>
                                    <div>
                                        <Title level={4} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                                            Social Media Links
                                        </Title>
                                        <Text type="secondary" style={{ fontSize: '15px', color: '#595959', display: 'block', marginTop: 4 }}>
                                            Connect your social media profiles
                                        </Text>
                                    </div>
                                </Space>
                            </div>
                            <Form.Item 
                                label={
                                    <Space size="small">
                                        <span style={{ fontWeight: 500, fontSize: '15px' }}>Social Media Links</span>
                                        <Tooltip title="Add links to your company's social media profiles (Facebook, Instagram, Twitter, LinkedIn, etc.)">
                                            <QuestionCircleOutlined style={{ color: '#bfbfbf', fontSize: '14px', cursor: 'help' }} />
                                        </Tooltip>
                                    </Space>
                                }
                                extra={
                                    <Text type="secondary" style={{ fontSize: '13px' }}>
                                        🔗 Add links to your social media profiles (optional)
                                    </Text>
                                }
                            >
                                {socialMediaLinks.map((link, index) => (
                                    <Space key={index} style={{ display: 'flex', marginBottom: 12 }} align="baseline">
                                        <Form.Item
                                            className="social-media-link-item"
                                            style={{ flex: 1, marginBottom: 0 }}
                                            validateStatus={link && link.trim() !== '' && !validateURL(link) ? 'error' : ''}
                                            help={link && link.trim() !== '' && !validateURL(link) ? 'Please enter a valid URL (e.g., https://facebook.com/yourpage)' : ''}
                                        >
                                            <Input
                                                prefix={<LinkOutlined style={{ color: '#722ed1' }} />}
                                                placeholder="https://facebook.com/yourpage"
                                                value={link}
                                                onChange={(e) => updateSocialMediaLink(index, e.target.value)}
                                                onBlur={(e) => {
                                                    // Validate on blur
                                                    if (e.target.value && e.target.value.trim() !== '' && !validateURL(e.target.value)) {
                                                        message.warning('Please enter a valid URL');
                                                    }
                                                }}
                                                size="large"
                                                style={{ borderRadius: '8px' }}
                                            />
                                        </Form.Item>
                                        {socialMediaLinks.length > 1 && (
                                            <Button 
                                                type="link" 
                                                danger 
                                                onClick={() => removeSocialMediaLink(index)}
                                                icon={<MinusCircleOutlined />}
                                                size="large"
                                            >
                                                Remove
                                            </Button>
                                        )}
                                    </Space>
                                ))}
                                <Button 
                                    type="dashed" 
                                    onClick={addSocialMediaLink}
                                    icon={<PlusOutlined />}
                                    size="large"
                                    style={{ 
                                        width: '100%',
                                        borderRadius: '8px',
                                        height: '48px',
                                        borderStyle: 'dashed',
                                        borderWidth: '2px',
                                        borderColor: '#722ed1',
                                        color: '#722ed1',
                                        fontWeight: 500
                                    }}
                                >
                                    <PlusOutlined style={{ marginRight: 8 }} />
                                    Add Another Social Media Link
                                </Button>
                            </Form.Item>
                        </Card>

                        <Divider style={{ margin: '40px 0', borderColor: '#e8f4f8' }} />

                        <div style={{ 
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 16,
                            paddingTop: 24
                        }}>
                            <div>
                                <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
                                    💡 Review your changes and click "Save Company Information" when everything looks good!
                                </Typography.Text>
                            </div>
                            <Button
                                type="primary"
                                size="large"
                                htmlType="submit"
                                loading={loading}
                                icon={<SaveOutlined />}
                                style={{ 
                                    minWidth: 220,
                                    height: '44px',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                                }}
                            >
                                {loading ? 'Saving...' : '✅ Save Company Information'}
                            </Button>
                        </div>
                        </Form>
            </Spin>
        </DashboardLayout>
    );
};

export default CompanyInformation;
